import { IMG } from "@/lib/assets";

// Erlebnisräume — patrice.studio's "inner rooms". Final canon: 5 rooms.
// Each room has its own peony image, accent colour and atmosphere.

export const ERLEBNISRAEUME = [
  // ============================================================
  // 01 · FOTO-RAUM (active)
  // ============================================================
  {
    id: "foto-raum",
    number: "01",
    name: { de: "Foto-Raum", en: "Photo room" },
    state: "active",
    image: IMG.peonyFoto,
    accent: { hex: "#5C4752", name: { de: "Atmosphäre", en: "Atmosphere" } },
    teaser: {
      de: "Licht, in dem etwas atmet.",
      en: "Light, where something breathes.",
    },
    eyebrow: { de: "Erlebnisraum · Foto-Raum", en: "Inner room · Photo room" },
    titleItalic: { de: "Licht,", en: "Light," },
    titleRest: { de: "in dem etwas atmet.", en: "where something breathes." },
    lede: {
      de: "Eine eigene Bildwelt — fotografiert in dem Moment, in dem das Licht selbst zur Geschichte wird.",
      en: "A visual library of my own — photographed in the moment light itself becomes the story.",
    },
    atmosphere: {
      de: "Dramatisch. Zart. Lebendig. Je nachdem, was der Moment verlangt.",
      en: "Dramatic. Delicate. Alive. Depending on what the moment demands.",
    },
    closing: {
      de: "Fotografie ist für mich die Art, wie ich der Welt zuhöre.",
      en: "Photography is the way I listen to the world.",
    },
    // FOTO-RAUM GALLERY · Patrice's own photographs.
    // To add a new photo: paste its URL into a new `{ src: "...", aspect: "16/9" }` entry.
    // `aspect` options: "16/9" (wide), "3/2" (landscape), "4/3" (classic), "1/1" (square), "4/5" or "3/4" (portrait).
    capacity: 30,
    works: [
      // 01 · Wiese mit Bank in goldenen Wolken (Hero-Bild des Raums in Originalformat)
      {
        src: "https://customer-assets.emergentagent.com/job_peony-redesign/artifacts/6q1xmmek_DSC00604_1a.jpg",
        aspect: "3/2",
        caption: { de: "Wenn das Licht für einen Moment Geschichte wird.", en: "When light becomes a story for a moment." },
      },
      // 02 · Rundbogentür (alte Tür mit Blume, vorheriges Foto-Raum-Bild)
      {
        src: "https://customer-assets.emergentagent.com/job_peony-redesign/artifacts/3ibo4o1v_IMG_5509_1%20f%C3%BCr%20Emmi.jpg",
        aspect: "3/4",
        caption: { de: "Eine Tür, die nichts erklärt.", en: "A door that explains nothing." },
      },
      // weitere Plätze — bleiben elegant leer (Foto folgt), bis du sie füllst.
      ...Array.from({ length: 28 }, (_, i) => ({
        src: null,
        aspect: ["4/5", "3/4", "1/1", "4/5", "3/4", "1/1"][i % 6],
      })),
    ],
  },

  // ============================================================
  // 02 · KUNST-RAUM (active)
  // ============================================================
  {
    id: "kunst-raum",
    number: "02",
    name: { de: "Kunst-Raum", en: "Art room" },
    state: "active",
    image: IMG.peonyKunst,
    accent: { hex: "#28001B", name: { de: "Tiefe", en: "Depth" } },
    teaser: {
      de: "Was die Hand auf Leinwand sagt.",
      en: "What the hand says on canvas.",
    },
    eyebrow: { de: "Erlebnisraum · Kunst-Raum", en: "Inner room · Art room" },
    titleItalic: { de: "Was die Hand", en: "What the hand" },
    titleRest: { de: "auf Leinwand sagt.", en: "says on canvas." },
    lede: {
      de: "Strukturen, Schichten, Spuren — gemalt zwischen Atemzügen. Was nicht in Worte passt, findet hier seine Form.",
      en: "Textures, layers, traces — painted between breaths. What doesn’t fit into words finds its form here.",
    },
    atmosphere: {
      de: "Abstrakt. Atmosphärisch. Strukturiert.",
      en: "Abstract. Atmospheric. Structured.",
    },
    closing: {
      de: "Manche Bilder entstehen, weil ich sie verstehen will. Andere entstehen, weil ich aufhöre, sie verstehen zu wollen.",
      en: "Some paintings are born because I want to understand them. Others are born when I stop trying.",
    },
    // 15 slots available for paintings — no example titles, no placeholder names
    capacity: 15,
    works: Array.from({ length: 15 }, (_, i) => ({
      src: null,
      aspect: ["4/5", "3/4", "1/1", "4/5", "3/4"][i % 5],
    })),
  },

  // ============================================================
  // 03 · KLANG-RAUM (coming soon)
  // ============================================================
  {
    id: "klang-raum",
    number: "03",
    name: { de: "Klang-Raum", en: "Sound room" },
    state: "coming_soon",
    image: IMG.peonyKlang,
    accent: { hex: "#8B6B7A", name: { de: "Tonfeld", en: "Tonefield" } },
    teaser: {
      de: "Musik, die aus Leidenschaft entsteht.",
      en: "Music born from passion.",
    },
    eyebrow: { de: "Erlebnisraum · Klang-Raum", en: "Inner room · Sound room" },
    titleItalic: { de: "Musik,", en: "Music," },
    titleRest: { de: "die aus Leidenschaft entsteht.", en: "born from passion." },
    lede: {
      de: "Musik, die ich fühle, bevor ich sie mache. Produziert mit Suno, zu hören auf Spotify, Apple Music und Amazon. Ein Klangraum, der gerade seine Stimme findet.",
      en: "Music I feel before I make it. Produced with Suno, available on Spotify, Apple Music and Amazon. A sound space that is currently finding its voice.",
    },
    atmosphere: {
      de: "Leidenschaftlich. Ungefiltert. Echt.",
      en: "Passionate. Unfiltered. Real.",
    },
    promise: {
      de: "Sobald die ersten Stücke veröffentlicht sind, öffne ich diesen Raum vollständig.",
      en: "Once the first tracks are out, this room will fully open.",
    },
    closing: {
      de: "Musik war immer in mir. Jetzt findet sie ihren Weg nach draußen.",
      en: "Music was always inside me. Now it finds its way out.",
    },
  },

  // ============================================================
  // 04 · KREATIV-RAUM (coming soon)
  // ============================================================
  {
    id: "kreativ-raum",
    number: "04",
    name: { de: "Kreativ-Raum", en: "Creative room" },
    state: "coming_soon",
    image: IMG.peonyKreativ,
    accent: { hex: "#FF7100", name: { de: "Signal", en: "Signal" } },
    teaser: {
      de: "Worte, die hängen bleiben.",
      en: "Words that linger.",
    },
    eyebrow: { de: "Erlebnisraum · Kreativ-Raum", en: "Inner room · Creative room" },
    titleItalic: { de: "Worte,", en: "Words," },
    titleRest: { de: "die hängen bleiben.", en: "that linger." },
    lede: {
      de: "T-Shirts, Taschen, Poster und Drucke — mit Sprüchen, die das Leben kennen. Mal zum Lachen, mal zum Nachdenken, immer mit Haltung.",
      en: "T-shirts, tote bags, posters and prints — with words that know life. Sometimes to laugh, sometimes to reflect, always with stance.",
    },
    atmosphere: {
      de: "Humorvoll. Direkt. Manchmal frech — immer wertig.",
      en: "Witty. Direct. Sometimes cheeky — always refined.",
    },
    promise: {
      de: "Sobald die ersten Designs in Produktion gehen, lade ich dich in diesen Raum ein.",
      en: "Once the first designs are in production, I’ll invite you in.",
    },
    closing: {
      de: "Manche Wahrheiten gehören auf einen Beutel — nicht in eine Powerpoint.",
      en: "Some truths belong on a tote — not in a deck.",
    },
  },

  // ============================================================
  // 05 · ATELIER-RAUM (coming soon)
  // ============================================================
  {
    id: "atelier-raum",
    number: "05",
    name: { de: "Atelier-Raum", en: "Atelier room" },
    state: "coming_soon",
    image: IMG.peonyAtelier,
    accent: { hex: "#CBB7B0", name: { de: "Wärme", en: "Warmth" } },
    teaser: {
      de: "Farbe ist Emotion. Farbe ist Stimmung. Farbe ist alles.",
      en: "Colour is emotion. Colour is mood. Colour is everything.",
    },
    eyebrow: { de: "Erlebnisraum · Atelier-Raum", en: "Inner room · Atelier room" },
    titleItalic: { de: "Farbe", en: "Colour" },
    titleRest: { de: "ist alles.", en: "is everything." },
    lede: {
      de: "Farbpaletten, Moodboards, Brand Kits & Digitale Werke — entstanden aus echter Bildwelt, nicht aus dem Farbtopf der Trends.",
      en: "Palettes, moodboards, brand kits & digital works — born from a real visual library, not from the paint pot of trends.",
    },
    atmosphere: {
      de: "Präzise. Intuitiv. Unverwechselbar.",
      en: "Precise. Intuitive. Unmistakable.",
    },
    promise: {
      de: "Sobald die ersten Paletten kuratiert sind, öffnet sich dieser Raum.",
      en: "Once the first palettes are curated, this room will open.",
    },
    closing: {
      de: "Jede Farbe erzählt etwas. Die Frage ist nur — was soll deine sagen?",
      en: "Every colour tells something. The only question is — what should yours say?",
    },
  },
];
